import moment from 'moment';

// Helpers for the grouped download-request views (#9 minimal package).
//
// The list endpoint (`grouped: true`) returns flat rows ordered by group, each
// row carrying its group's key/status/count/date. These helpers reassemble the
// groups for rendering and hold the label/badge vocabulary shared by the
// requester's My Requests page and the approver's Download Requests page.

export const GROUP_STATUSES = ['PENDING', 'PARTIALLY_APPROVED', 'APPROVED', 'REJECTED'];

export const GROUP_STATUS_LABEL = {
    PENDING: 'Pending',
    PARTIALLY_APPROVED: 'Partially Approved',
    APPROVED: 'Approved',
    REJECTED: 'Rejected',
};

// Per-asset (row) status → label. Rows use the legacy OPEN vocabulary.
export const ROW_STATUS_LABEL = {
    OPEN: 'Pending',
    APPROVED: 'Approved',
    REJECTED: 'Rejected',
};

const BADGE_CLASS = {
    PENDING: 'status-badge--pending',
    OPEN: 'status-badge--pending',
    PARTIALLY_APPROVED: 'status-badge--processing',   // blue, distinct from amber Pending (mockup)
    APPROVED: 'status-badge--approved',
    REJECTED: 'status-badge--rejected',
};

export function statusBadgeClass(status) {
    return BADGE_CLASS[status] || 'status-badge--neutral';
}

// Colour tone of a status — shared by the request pill, the per-asset
// dot + word, and the approver's decision dropdown, so one status always
// reads in one colour wherever it appears.
const TONE = { PENDING: 'pending', OPEN: 'pending', PARTIALLY_APPROVED: 'partial', APPROVED: 'approved', REJECTED: 'rejected' };

export function statusTone(status) {
    return TONE[status] || 'neutral';
}

// Rows → ordered groups. Server order is preserved (rows of one group arrive
// contiguously); a group's header fields are read from its first row. Legacy
// rows (no batch id) are flagged so the UI can label the card.
export function groupRequestRows(rows) {
    const groups = [];
    const byKey = new Map();
    for (const row of rows || []) {
        const key = row.groupKey;
        let g = byKey.get(key);
        if (!g) {
            g = {
                groupKey: key,
                groupStatus: row.groupStatus,
                assetCount: Number(row.assetCount) || 0,
                groupRequestedDate: Number(row.groupRequestedDate) || null,
                projectName: row.projectName ?? null,
                projectBrief: row.projectBrief ?? null,
                usageTypes: row.usageTypes ?? null,
                requesterName: row.videoRequestedByName ?? null,
                userId: row.user_id ?? null,
                isLegacy: !row.requestBatchId,
                rows: [],
            };
            byKey.set(key, g);
            groups.push(g);
        }
        g.rows.push(row);
    }
    return groups;
}

// The fixed usage channels offered by the request form; "Other: <text>" is
// the free-text entry. (Single source for the form and the parsers.)
export const USAGE_OPTIONS = ['Social Media', 'TV/Broadcast', 'Website/Digital', 'Print', 'Internal'];
export const OTHER_PREFIX = 'Other: ';
export const OTHER_MAX = 60;

// Usage types are stored comma-joined ("Social Media, TV/Broadcast, Other: x").
// New writes strip commas from the Other text, but older rows may carry
// them — any segment that is neither a known channel nor an "Other:" entry
// is glued back onto the preceding "Other:" entry instead of becoming a
// bogus extra chip.
export function splitUsageTypes(usageTypes) {
    if (typeof usageTypes !== 'string') return [];
    const out = [];
    for (const raw of usageTypes.split(',')) {
        const seg = raw.trim();
        if (!seg) continue;
        const known = USAGE_OPTIONS.includes(seg) || seg.startsWith(OTHER_PREFIX);
        if (!known && out.length && out[out.length - 1].startsWith(OTHER_PREFIX)) {
            out[out.length - 1] += `, ${seg}`;
        } else {
            out.push(seg);
        }
    }
    return out;
}

// Card-header date: "10 Sep 2026" reads faster than 10-09-2026 when scanning
// a column of them (the tables inside a card keep DD-MM-YYYY).
export function formatRequestDate(ms) {
    const n = Number(ms);
    return n ? moment(n).format('D MMM YYYY') : '';
}

export function assetCountLabel(count) {
    const n = Number(count) || 0;
    return `${n} asset${n === 1 ? '' : 's'}`;
}

// Card title: the project name, or the day for a legacy (pre-form) request.
export function requestGroupTitle(g) {
    return g.isLegacy
        ? `Requests of ${formatRequestDate(g.groupRequestedDate)}`
        : (g.projectName || 'Untitled request');
}

// video_pipeline.file_size is MEGABYTES (the app reads it as MB everywhere:
// ListVideo totals, share page, `${file_size} MB` labels; the processing
// pipeline writes ffprobe's MB value).
export function formatFileSize(mb) {
    const n = Number(mb);
    if (!Number.isFinite(n) || n <= 0) return '';
    if (n >= 1024) return `${(n / 1024).toFixed(1)} GB`;
    if (n >= 1) return `${n >= 100 ? Math.round(n) : n.toFixed(1)} MB`;
    return `${Math.max(1, Math.round(n * 1024))} KB`;
}
